// import * as firebase from 'firebase'
import { db, auth, timestamp } from "@/firebase";
import {
  signInWithCustomToken,
  onAuthStateChanged,
  signOut
} from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  limit,
  onSnapshot
} from "firebase/firestore";
import Axios from "axios";
import { apiDomain } from "../../config";
import router from "../../router";
import moment from "moment";
import EncryptionService from "@/services/encryptionService";

// Helper function to decrypt member data with legacy support
const decryptMemberData = memberData => {
  if (!memberData) return memberData;

  const decrypted = { ...memberData };

  // Decrypt sensitive fields if they exist
  const fieldsToDecrypt = [
    "email",
    "firstname",
    "lastname",
    "mobile",
    "accountKey"
  ];

  fieldsToDecrypt.forEach(field => {
    if (decrypted[field] && typeof decrypted[field] === "string") {
      try {
        // Use safeDecrypt which handles both encrypted and plain text data
        decrypted[field] = EncryptionService.safeDecrypt(decrypted[field]);
      } catch (error) {
        console.warn(`Failed to decrypt field ${field}:`, error.message);
        // Keep original value if decryption fails (legacy data compatibility)
      }
    }
  });

  return decrypted;
};

/**
 * Read the member identity out of a Firebase session's claims.
 *
 * A member session minted by /auth/signin names its member and program, so the
 * member can be restored on a refresh without querying the members collection
 * by email. Anonymous and demo sessions carry no such claims and return null,
 * leaving the caller to fall back to its previous behaviour.
 *
 * The program is checked as well: a session issued for one program must not
 * restore a member on another program's domain, since the pods share a
 * Firebase project and a token is otherwise valid across all of them.
 */
const memberIdFromClaims = async (firebaseUser, programId) => {
  try {
    const { claims } = await firebaseUser.getIdTokenResult();

    if (!claims || !claims.member) {
      return null;
    }

    if (claims.programId !== programId) {
      console.warn(
        "⚠️ Member session belongs to a different program - ignoring claims"
      );
      return null;
    }

    return claims.memberId || null;
  } catch (error) {
    console.warn("Could not read member session claims:", error.message);
    return null;
  }
};

// Helper function to find user by email (handles both encrypted and legacy data)
const findUserByEmail = async (membersRef, email) => {
  console.log("🔍 Searching for user by email:", email);

  // First try with encrypted email (for new data)
  try {
    const encryptedEmail = EncryptionService.encrypt(email);
    const encryptedQuery = query(
      membersRef,
      where("email", "==", encryptedEmail)
    );
    const encryptedSnapshot = await getDocs(encryptedQuery);

    if (!encryptedSnapshot.empty) {
      console.log("✅ Found user with encrypted email");
      return encryptedSnapshot;
    }
  } catch (error) {
    console.warn("❌ Encrypted email query failed:", error);
  }

  // If not found, try with plain text email (for legacy data)
  try {
    const plainTextQuery = query(membersRef, where("email", "==", email));
    const plainTextSnapshot = await getDocs(plainTextQuery);

    if (!plainTextSnapshot.empty) {
      console.log("✅ Found user with plain text email (legacy data)");
      return plainTextSnapshot;
    }
  } catch (error) {
    console.warn("❌ Plain text email query failed:", error);
  }

  // If direct queries fail, search all auth docs and decrypt to find matches
  try {
    const allMembersQuery = query(membersRef);
    const allMembersSnapshot = await getDocs(allMembersQuery);

    let comprehensiveFoundMatch = false;
    let matchedMemberIds = [];

    allMembersSnapshot.forEach(doc => {
      const data = doc.data();

      // Check plain text email
      if (data.email === email) {
        comprehensiveFoundMatch = true;
        matchedMemberIds.push(doc.id);
      }

      // Check encrypted email
      if (data.email && data.email.length > 50) {
        try {
          const decrypted = EncryptionService.safeDecrypt(data.email);
          if (decrypted === email) {
            comprehensiveFoundMatch = true;
            matchedMemberIds.push(doc.id);
          }
        } catch (error) {
          // Ignore decryption errors for comprehensive search
        }
      }
    });

    if (comprehensiveFoundMatch) {
      console.log("✅ Found user via comprehensive search");
      // Return the first matched user from comprehensive search
      const matchedDoc = allMembersSnapshot.docs.find(doc =>
        matchedMemberIds.includes(doc.id)
      );
      if (matchedDoc) {
        return { empty: false, docs: [matchedDoc], size: 1 };
      }
    }
  } catch (error) {
    console.error("❌ Comprehensive search failed:", error);
  }

  console.log("❌ User not found with either encrypted or plain text email");
  return { empty: true, docs: [] };
};

// Helper function to check for duplicates (handles both encrypted and legacy data)
const checkForDuplicates = async (membersRef, email, accountKey = null) => {
  let emailDuplicates = { empty: true, size: 0 };
  let accountKeyDuplicates = { empty: true, size: 0 };

  // Check email duplicates (both encrypted and plain text)
  try {
    const emailQuerySnapshot = await findUserByEmail(membersRef, email);
    emailDuplicates = emailQuerySnapshot;
  } catch (error) {
    console.warn("Error checking email duplicates:", error);
  }

  // Check accountKey duplicates if provided
  if (accountKey) {
    try {
      // Try encrypted accountKey first
      const encryptedAccountKey = EncryptionService.encrypt(accountKey);
      const encryptedKeyQuery = query(
        membersRef,
        where("accountKey", "==", encryptedAccountKey)
      );
      const encryptedKeySnapshot = await getDocs(encryptedKeyQuery);

      if (!encryptedKeySnapshot.empty) {
        accountKeyDuplicates = encryptedKeySnapshot;
      } else {
        // Try plain text accountKey (legacy data)
        const plainKeyQuery = query(
          membersRef,
          where("accountKey", "==", accountKey)
        );
        const plainKeySnapshot = await getDocs(plainKeyQuery);

        if (!plainKeySnapshot.empty) {
          accountKeyDuplicates = plainKeySnapshot;
        }
      }
    } catch (error) {
      console.warn("Error checking accountKey duplicates:", error);
    }
  }

  return { emailDuplicates, accountKeyDuplicates };
};

const state = {
  currentUser: null,
  programId: null,
  userDealer: [],
  adminToken: null,
  email: "",
  token: null,
  loading: false,
  registrationSuccess: false,
  registrationErrors: [],
  userUnsubscribe: null,
  cleanupInProgress: false,
  isAuthenticating: false, // Track if user is actively signing in
  lastUserRefresh: null // Track when user data was last refreshed
};

const actions = {
  async getCurrentUser({ commit, dispatch, getters }) {
    console.log("🚀 getCurrentUser called");
    const programId = getters.programId;

    if (!programId) {
      console.log("❌ No programId available");
      return null;
    }

    // Check if we already have persisted user data first
    const persistedUser = getters.currentUser;

    if (persistedUser && persistedUser._id && persistedUser.email) {
      // We have persisted user data, but let's fetch fresh data first
      const userId = persistedUser._id;
      const programRef = doc(db(), "programs", programId);

      // Clean up any existing user subscription first
      const existingUnsubscribe = getters.userUnsubscribe;
      if (existingUnsubscribe) {
        existingUnsubscribe();
        commit("setUserUnsubscribe", null);
      }

      // Fetch fresh user data first
      const userRef = doc(collection(programRef, "members"), userId);

      try {
        const freshUserSnapshot = await getDoc(userRef);
        if (freshUserSnapshot.exists()) {
          const rawUserData = freshUserSnapshot.data();
          // Decrypt sensitive data before processing
          const freshUserData = decryptMemberData(rawUserData);

          // Update the persisted user with fresh data
          const updatedPersistedUser = {
            ...freshUserData,
            _id: freshUserSnapshot.id,
            phone: freshUserData.phone ? freshUserData.phone : "-",
            avatar: freshUserData.externalImageUrl
              ? freshUserData.externalImageUrl
              : freshUserData.image?.smallUrl || null
          };

          commit("setCurrentUser", updatedPersistedUser);
          commit("setLastUserRefresh", Date.now());
        }
      } catch (error) {
        console.error("❌ Error fetching fresh persisted user data:", error);
      }

      // Set up real-time listener for the persisted user
      const userUnsubscribe = onSnapshot(
        userRef,
        async docSnapshot => {
          if (docSnapshot.exists()) {
            const rawUserData = docSnapshot.data();
            // Decrypt sensitive data before processing
            const userData = decryptMemberData(rawUserData);

            // Get company tags
            let companyTags = [];
            if (userData.companies && Array.isArray(userData.companies)) {
              try {
                const companiesSnapshot = await Promise.all(
                  userData.companies.map(companyId => {
                    const companyRef = doc(
                      collection(programRef, "companies"),
                      companyId
                    );
                    return getDoc(companyRef);
                  })
                );
                companiesSnapshot.forEach(snapshot => {
                  if (snapshot.exists()) {
                    const companyData = snapshot.data();
                    if (companyData.tags && Array.isArray(companyData.tags)) {
                      companyData.tags.forEach(tag => {
                        if (companyTags.indexOf(tag) === -1) {
                          companyTags.push(tag);
                        }
                      });
                    }
                  }
                });
              } catch (error) {
                console.error("Error fetching company tags:", error);
              }
            }

            const restoredUser = {
              ...userData,
              companyTags,
              _id: docSnapshot.id,
              phone: userData.phone ? userData.phone : "-",
              avatar: userData.externalImageUrl
                ? userData.externalImageUrl
                : userData.image?.smallUrl || null
            };

            commit("setCurrentUser", restoredUser);
          }
        },
        error => {
          console.error("Error in user snapshot listener:", error);
          if (userUnsubscribe) {
            userUnsubscribe();
          }
        }
      );

      // Store the unsubscribe function for cleanup
      commit("setUserUnsubscribe", userUnsubscribe);

      // Return the updated user (or persisted user if fetch failed)
      return getters.currentUser || persistedUser;
    }

    // If no persisted user, fall back to Firebase auth-based lookup
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      return null;
    }

    // If the Firebase user is anonymous and has no email, and we have no persisted data, skip
    if (firebaseUser.isAnonymous && !firebaseUser.email) {
      return null;
    }

    // Clean up any existing user subscription first
    const existingUnsubscribe = getters.userUnsubscribe;
    if (existingUnsubscribe) {
      existingUnsubscribe();
      commit("setUserUnsubscribe", null);
    }

    // Check if we have a current user in store first
    let currentUser = getters.currentUser;
    let userId = null;

    if (currentUser && currentUser._id) {
      userId = currentUser._id;
    } else {
      // A member session already names its member, so prefer that over
      // searching the members collection. It is both cheaper and the only
      // option that survives the members collection being closed to
      // unidentified callers.
      userId = await memberIdFromClaims(firebaseUser, programId);
    }

    // Only worth attempting where there is an email to search on. A member
    // session carries no email address, so without claims there is nothing to
    // look up and the query would scan the collection for null.
    if (!userId && firebaseUser.email) {
      // If no store user, try to find user by Firebase email
      try {
        const programRef = doc(db(), "programs", programId);
        const membersRef = collection(programRef, "members");
        // Use helper function to find user (handles both encrypted and legacy data)
        const querySnapshot = await findUserByEmail(
          membersRef,
          firebaseUser.email
        );

        if (!querySnapshot.empty) {
          querySnapshot.forEach(docSnapshot => {
            userId = docSnapshot.id;
          });
        } else {
          return null;
        }
      } catch (error) {
        console.error("Error finding user by email:", error);
        return null;
      }
    }

    if (!userId) {
      return null;
    }

    try {
      const programRef = doc(db(), "programs", programId);
      let companyTags = [];

      // First get company tags if user has companies (only if we have currentUser data)
      if (
        currentUser &&
        currentUser.companies &&
        Array.isArray(currentUser.companies)
      ) {
        try {
          const companiesSnapshot = await Promise.all(
            currentUser.companies.map(companyId => {
              const companyRef = doc(
                collection(programRef, "companies"),
                companyId
              );
              return getDoc(companyRef);
            })
          );
          companiesSnapshot.forEach(snapshot => {
            if (snapshot.exists()) {
              const companyData = snapshot.data();
              if (companyData.tags && Array.isArray(companyData.tags)) {
                companyData.tags.forEach(tag => {
                  if (companyTags.indexOf(tag) === -1) {
                    companyTags.push(tag);
                  }
                });
              }
            }
          });
        } catch (error) {
          console.error("Error fetching company tags:", error);
        }
      }

      // Get the current user data immediately (for auth guard)
      const userRef = doc(collection(programRef, "members"), userId);
      const userSnapshot = await getDoc(userRef);

      if (!userSnapshot.exists()) {
        console.log("❌ User document does not exist");
        return null;
      }

      const rawUserData = userSnapshot.data();
      // Decrypt sensitive data before processing
      const userData = decryptMemberData(rawUserData);

      // Get company tags if we didn't have them before
      if (
        (!companyTags || companyTags.length === 0) &&
        userData.companies &&
        Array.isArray(userData.companies)
      ) {
        try {
          const companiesSnapshot = await Promise.all(
            userData.companies.map(companyId => {
              const companyRef = doc(
                collection(programRef, "companies"),
                companyId
              );
              return getDoc(companyRef);
            })
          );
          companiesSnapshot.forEach(snapshot => {
            if (snapshot.exists()) {
              const companyData = snapshot.data();
              if (companyData.tags && Array.isArray(companyData.tags)) {
                companyData.tags.forEach(tag => {
                  if (companyTags.indexOf(tag) === -1) {
                    companyTags.push(tag);
                  }
                });
              }
            }
          });
        } catch (error) {
          console.error("Error fetching company tags immediately:", error);
        }
      }

      const immediateUser = {
        ...userData,
        companyTags,
        _id: userSnapshot.id,
        phone: userData.phone ? userData.phone : "-",
        avatar: userData.externalImageUrl
          ? userData.externalImageUrl
          : userData.image?.smallUrl || null
      };

      // Set the user immediately
      commit("setCurrentUser", immediateUser);

      // Now set up real-time listener for future updates
      const userUnsubscribe = onSnapshot(
        userRef,
        async docSnapshot => {
          if (docSnapshot.exists()) {
            const rawUserData = docSnapshot.data();
            // Decrypt sensitive data before processing
            const userData = decryptMemberData(rawUserData);

            // Get company tags if we didn't have them before
            let updatedCompanyTags = [...companyTags];
            if (userData.companies && Array.isArray(userData.companies)) {
              try {
                const companiesSnapshot = await Promise.all(
                  userData.companies.map(companyId => {
                    const companyRef = doc(
                      collection(programRef, "companies"),
                      companyId
                    );
                    return getDoc(companyRef);
                  })
                );
                updatedCompanyTags = [];
                companiesSnapshot.forEach(snapshot => {
                  if (snapshot.exists()) {
                    const companyData = snapshot.data();
                    if (companyData.tags && Array.isArray(companyData.tags)) {
                      companyData.tags.forEach(tag => {
                        if (updatedCompanyTags.indexOf(tag) === -1) {
                          updatedCompanyTags.push(tag);
                        }
                      });
                    }
                  }
                });
              } catch (error) {
                console.error(
                  "Error fetching company tags in snapshot:",
                  error
                );
              }
            }

            const updatedUser = {
              ...userData,
              companyTags: updatedCompanyTags,
              _id: docSnapshot.id,
              phone: userData.phone ? userData.phone : "-",
              avatar: userData.externalImageUrl
                ? userData.externalImageUrl
                : userData.image?.smallUrl || null
            };
            commit("setCurrentUser", updatedUser);
          } else {
            console.log("❌ User document no longer exists");
            commit("setCurrentUser", null);
          }
        },
        error => {
          console.error("❌ Error in user snapshot listener:", error);
          if (userUnsubscribe) {
            userUnsubscribe();
          }
        }
      );

      // Store the unsubscribe function for cleanup
      commit("setUserUnsubscribe", userUnsubscribe);

      // Return the user object for immediate use (like in auth guard)
      return immediateUser;
    } catch (error) {
      return null;
    }
  },

  revalAdmin({ commit, getters, dispatch }) {
    dispatch("setSnackbar", null);
    const expiry = moment.unix(getters.adminToken.expiry.seconds).toDate();
    const now = moment().toDate();
    if (now > expiry) {
      commit("setCurrentUser", null);
      commit("setAdminToken", null);
      router.push("../../signin");
      // dispatch('setSnackbar', 'Your session has expired')
    } else {
      return "ok";
    }
  },

  /**
   * Trade an admin preview link for a member session.
   *
   * The token used to be resolved here: an anonymous sign-in, then a read of the
   * entire tokens collection to find the one named in the URL. Any authenticated
   * principal may read that collection, so signing in anonymously was enough to
   * obtain a list of live preview credentials -- and the token *is* the
   * credential, so that was a list of ways to become other members. The expiry
   * check that followed was no safeguard either, since it ran on data the caller
   * already held.
   *
   * The server resolves the token now and answers with a session that names the
   * member, so nothing here reads tokens.
   */
  async adminAuthFirebase({ commit, dispatch }, payload) {
    dispatch("setSnackbar", null);
    // The browser usually still holds the previous preview's member and
    // session. Until this member is loaded, App.vue must not restore a member
    // from that session, and requireMemberSession must not judge the
    // half-switched state.
    //
    // The stored member is deliberately left in place until adminAuth replaces
    // it: App.vue renders this route inside core-view when a member is stored
    // and on its own when not, so clearing it remounts Demo.vue, which starts
    // the preview again.
    commit("setIsAuthenticating", true);
    try {
      return await dispatch("startPreviewSession", payload);
    } finally {
      commit("setIsAuthenticating", false);
    }
  },

  async startPreviewSession({ commit, dispatch, getters }, payload) {
    let session;
    try {
      const res = await Axios.post(apiDomain + "/auth/demo-session", {
        programId: payload.pid,
        token: payload.token,
        // Null on this route: the demo branch of setProgramId never resolves a
        // regional database, so the server reads (default) -- the same database
        // the rest of the preview session uses. Previews of regional programs
        // do not work today for that reason, which predates this change.
        databaseId: getters.selectedDatabaseId
      });
      session = res.data;
    } catch (error) {
      // The server says the same thing for an unknown token, an expired one and
      // one naming a deleted member, deliberately.
      const message = error.response
        ? error.response.data
        : "Could not start the preview session. Please try again later.";
      commit("setCurrentUser", null);
      commit("setAdminToken", null);
      dispatch("setSnackbar", message);
      throw new Error(message);
    }

    try {
      await signInWithCustomToken(auth, session.customToken);
    } catch (error) {
      commit("setCurrentUser", null);
      commit("setAdminToken", null);
      console.error("Preview session sign-in failed:", error);
      dispatch("setSnackbar", "Could not start the preview session.");
      throw error;
    }

    // Demo.vue watches this to know the preview has started, and revalAdmin ends
    // the session once the window closes. Kept in the shape the token document
    // used to provide, since it is persisted and read back as plain JSON.
    commit("setAdminToken", {
      member: session.memberId,
      token: payload.token,
      expiry: { seconds: session.expiresAt }
    });

    return dispatch("adminAuth", { ...payload, memberId: session.memberId });
  },

  /**
   * Load the member a preview session was issued for.
   *
   * The member is named by the session, so this reads one document. It used to
   * be found by listing the tokens collection and matching the URL token against
   * every entry, and the token's expiry was checked here -- both of which now
   * happen on the server, before the session exists.
   */
  async adminAuth({ commit, dispatch }, payload) {
    try {
      dispatch("setSnackbar", null);
      const programRef = doc(db(), "programs", payload.pid);
      const memberRef = doc(
        collection(programRef, "members"),
        payload.memberId
      );

      const memberSnapshot = await getDoc(memberRef);
      if (!memberSnapshot.exists()) {
        // The server checks this too, so reaching here means the member was
        // removed between the exchange and this read.
        commit("setCurrentUser", null);
        commit("setAdminToken", null);
        const error = new Error("Member not found for demo session");
        dispatch("setSnackbar", "Member not found for demo session");
        throw error;
      }

      // Decrypt sensitive data before processing
      const member = decryptMemberData(memberSnapshot.data());
      const id = memberSnapshot.id;

      let companyTags = [];
      try {
        if (member.companies && Array.isArray(member.companies)) {
          const companiesSnapshot = await Promise.all(
            member.companies.map(companyId => {
              const companyRef = doc(
                collection(programRef, "companies"),
                companyId
              );
              return getDoc(companyRef);
            })
          );
          companiesSnapshot.map(snapshot => {
            if (snapshot.exists()) {
              const companyData = snapshot.data();
              companyData.tags &&
                companyData.tags.forEach(tag => {
                  if (companyTags.indexOf(tag) === -1) {
                    companyTags.push(tag);
                  }
                });
            }
          });
        }
      } catch (error) {
        console.error("Error fetching company tags:", error);
      }

      const currentMember = {
        ...member,
        companyTags,
        _id: id,
        phone: member.phone ? member.phone : "-",
        avatar: member.externalImageUrl
          ? member.externalImageUrl
          : member.image?.smallUrl || null
      };
      commit("setCurrentUser", currentMember);
      dispatch("setLoading", false);
      return currentMember;
    } catch (error) {
      console.error("Admin auth error:", error);
      commit("setCurrentUser", null);
      commit("setAdminToken", null);
      throw error;
    }
  },

  async signUserUpFirebase({ dispatch, commit }, payload) {
    commit("setIsAuthenticating", true);
    dispatch("setFirstTimeLoad", false);
    dispatch("setLoading", false);
    await dispatch("signUserUp", payload);
  },

  /**
   * Register through POST /auth/register.
   *
   * This used to run in the browser on an anonymous session, which needed the
   * rules to let any anonymous visitor list a program's members for the
   * duplicate check. The server now checks duplicates, decides the status and
   * balances, and writes the member and any new company.
   */
  async signUserUp({ commit, dispatch, getters }, payload) {
    commit("setRegistrationSuccess", false);
    commit("setRegistrationErrors", []);
    commit("setAdminToken", null);
    commit("setAuthLoading", true);

    try {
      const res = await Axios.post(apiDomain + "/auth/register", {
        ...payload,
        programId: getters.programId,
        databaseId: getters.selectedDatabaseId
      });
      dispatch("setSnackbar", res.data.message);
      commit("setRegistrationSuccess", true);
    } catch (err) {
      const message =
        err.response && typeof err.response.data === "string"
          ? err.response.data
          : "Registration could not be completed. Please try again later.";
      console.error("❌ Registration failed:", message);
      commit("setRegistrationErrors", message.split(", "));
    } finally {
      commit("setAuthLoading", false);
      commit("setIsAuthenticating", false);
    }
  },

  // No Firebase session is needed to check a password: /auth/signin is a plain
  // HTTP call, and its answer carries the member session to sign in with.
  async signUserInFirebase({ dispatch, commit }, payload) {
    commit("setIsAuthenticating", true);
    await dispatch("signUserIn", payload);
  },

  /**
   * Exchange the custom token from /auth/signin for a Firebase session that
   * identifies this member and their program.
   *
   * The rules admit members only on this session, so there is nothing to fall
   * back to: without it every read is refused.
   *
   * @returns {Promise<boolean>} whether a member session was established.
   */
  async upgradeToMemberSession(_, customToken) {
    if (!customToken) {
      console.error("❌ Sign-in response carried no member session token");
      return false;
    }

    try {
      await signInWithCustomToken(auth, customToken);
      console.log("✅ Member session established");
      return true;
    } catch (error) {
      console.error("❌ Could not establish member session:", error);
      return false;
    }
  },

  async signUserIn({ commit, dispatch, getters }, payload) {
    commit("setAdminToken", null);
    commit("setCurrentUser", null);
    commit("setAuthLoading", true);

    const failWith = message => {
      console.log(message);
      commit("setSnackbar", message);
      commit("setAuthLoading", false);
      commit("setIsAuthenticating", false);
    };

    if (!payload.email) {
      return failWith("Please enter your email");
    }

    if (!payload.password) {
      return failWith("Please enter your password");
    }

    const programRef = doc(db(), "programs", getters.programId);
    const membersRef = collection(programRef, "members");

    // The member document used to be read here, by email, to decide whether
    // this account was allowed to sign in. That read ran before
    // authentication, on an anonymous session, so it answered anyone who
    // asked whether an address was registered -- and it stops working once
    // members must be identified to read the collection. The server makes the
    // decision now, and returns the same wording members saw before.
    let res;
    try {
      res = await Axios.post(apiDomain + "/auth/signin", {
        email: payload.email,
        password: payload.password,
        programId: getters.programId,
        databaseId: getters.selectedDatabaseId
      });
    } catch (err) {
      const message = err.response
        ? err.response.data
        : "Unexpected Error Occured! Please try again later";
      console.error("❌ Authentication error:", message, err);
      return failWith(message);
    }

    console.log("✅ API authentication successful");
    commit("setToken", res.data.token);

    // Trade the anonymous session for one that names this member and their
    // program. Until now Firestore has only known that *someone* is signed in,
    // which anyone can achieve without a password.
    //
    // This has to happen before the listener below, so the snapshot is read
    // under the member's own identity rather than the anonymous one.
    // Re-authenticating afterwards would re-evaluate the rules on an
    // already-open listener.
    if (!(await dispatch("upgradeToMemberSession", res.data.customToken))) {
      await signOut(auth).catch(() => {});
      return failWith("Unable to sign in. Please try again later.");
    }

    // The server names who just authenticated, so the member no longer has to
    // be found by searching the collection for their email address.
    const memberId = res.data.memberId;
    if (!memberId) {
      console.error("❌ Sign-in response carried no member id");
      return failWith("Unable to verify credentials. Please try again.");
    }

    try {
      // Clean up any existing user subscription
      const existingUnsubscribe = getters.userUnsubscribe;
      if (existingUnsubscribe) {
        existingUnsubscribe();
        commit("setUserUnsubscribe", null);
      }

      const userDocRef = doc(membersRef, memberId);

      const unsubscribe = onSnapshot(
        userDocRef,
        async docSnapshot => {
          if (docSnapshot.exists()) {
            const rawUser = docSnapshot.data();
            // Decrypt sensitive data before processing
            let user = decryptMemberData(rawUser);

            let companyTags = [];
            try {
              if (user.companies && user.companies.length > 0) {
                const companiesSnapshot = await Promise.all(
                  user.companies.map(companyId => {
                    const companyRef = doc(
                      collection(programRef, "companies"),
                      companyId
                    );
                    return getDoc(companyRef);
                  })
                );

                companiesSnapshot.map(snapshot => {
                  if (snapshot.exists()) {
                    const companyData = snapshot.data();
                    companyData.tags &&
                      companyData.tags.forEach(tag => {
                        if (companyTags.indexOf(tag) === -1) {
                          companyTags.push(tag);
                        }
                      });
                  }
                });
              }
            } catch (error) {
              console.error("Error fetching company tags:", error);
            }

            user = {
              ...user,
              companyTags,
              _id: docSnapshot.id,
              phone: docSnapshot.data().phone ? docSnapshot.data().phone : "-",
              avatar: user.externalImageUrl
                ? user.externalImageUrl
                : user.image?.smallUrl || null
            };

            // Set the current user first
            commit("setCurrentUser", user);
            commit("setLastUserRefresh", Date.now());

            // Store the unsubscribe function
            commit("setUserUnsubscribe", unsubscribe);

            // Reset authentication flags after successful authentication
            commit("setAuthLoading", false);
            commit("setIsAuthenticating", false);

            console.log("✅ Authentication completed successfully");
          }
        },
        error => {
          console.error("❌ Firebase listener error:", error);
          if (unsubscribe) {
            unsubscribe();
          }
          commit("setAuthLoading", false);
        }
      );
    } catch (err) {
      // The password was already accepted by this point, so a failure here is
      // ours rather than the member's -- most likely the rules refusing the
      // member document read.
      console.error("❌ Error establishing the member listener:", err);
      failWith("Unable to verify credentials. Please try again.");
    }
  },

  async getUserDealer({ commit, getters }, user) {
    commit("setUserDealer", []);

    // Add null check for user
    if (!user || !user.companies) {
      return;
    }

    const programRef = doc(db(), "programs", getters.programId);
    let dealerMap = user.companies;
    if (dealerMap && dealerMap.length !== 0) {
      await Promise.all(
        dealerMap.map(val => {
          const companyRef = doc(collection(programRef, "companies"), val);
          return getDoc(companyRef);
        })
      ).then(queries => {
        const data = queries
          .map(query => {
            return query.exists() ? query.data().title : "";
          })
          .filter(title => title !== "");
        commit("setUserDealer", data);
      });
    }
  },

  /**
   * Sign out a browser whose stored member is not backed by that member's
   * session.
   *
   * The member is persisted to localStorage separately from the Firebase
   * session. A browser that signed in before member sessions existed, or whose
   * session exchange failed, holds an anonymous session beside a stored member:
   * it looks signed in, but the rules refuse every read. Signing out sends the
   * member back to sign in, which issues the right session.
   *
   * @param {import("firebase/auth").User|null} firebaseUser
   * @returns {Promise<boolean>} false if the browser was signed out.
   */
  async requireMemberSession({ getters, dispatch }, firebaseUser) {
    const member = getters.currentUser;
    if (!member || !member._id || getters.isAuthenticating) {
      return true;
    }
    // A preview link replaces whatever session the browser held, and signing
    // out here would navigate away from the link before it is used.
    if (router.currentRoute.path.startsWith("/demo")) {
      return true;
    }

    let claims = null;
    if (firebaseUser) {
      try {
        ({ claims } = await firebaseUser.getIdTokenResult());
      } catch (error) {
        // Most likely offline. Not evidence of a stale session.
        console.warn("Could not read session claims:", error.message);
        return true;
      }
    }

    const programId = getters.programId;
    const matches =
      !!claims &&
      claims.member === true &&
      claims.memberId === member._id &&
      (!programId || claims.programId === programId);
    if (matches) {
      return true;
    }

    console.warn(
      "⚠️ Stored member has no matching member session - signing out"
    );
    await dispatch("logout");
    return false;
  },

  async logout({ commit, getters, dispatch }) {
    // Add debugging to see what's calling logout
    console.log("🚨 LOGOUT CALLED");

    try {
      // Sign out from Firebase first
      await signOut(auth);
      console.log("🔓 Firebase signOut completed");
    } catch (error) {
      console.error("❌ Error during Firebase signOut:", error);
    }

    // Clean up user subscription before logout and clear user data
    dispatch("cleanupAuthListeners", true); // true = clear user data

    commit("setProgressData", []);
    commit("setLeaderBoardData", []);
    commit("setTableData", []);
    commit("setPromotionList", []);
    commit("setRewardList", []);
    commit("setPrimaryMenus", []);
    commit("setSecondaryMenus", []);
    commit("setCurrentUser", null);
    commit("setAdminToken", null);
    commit("setToken", null);
    commit("setRegistrationSuccess", false);
    commit("setUserUnsubscribe", null);
    commit("setIsAuthenticating", false); // Reset authentication flag

    // Navigate to signin page if not already there
    if (router.currentRoute.path !== "/signin") {
      console.log("🔄 Navigating to signin page after logout");
      router.push("/signin").catch(err => {
        // Ignore NavigationDuplicated errors
        if (err.name !== "NavigationDuplicated") {
          console.error("❌ Navigation error after logout:", err);
        }
      });
    }
  },

  setEmail({ commit }, payload) {
    commit("setEmail", payload);
  },

  setCurrentUser({ commit }, payload) {
    commit("setCurrentUser", payload);
  },

  setAdminToken({ commit }, payload) {
    commit("setAdminToken", payload);
  },

  setUserDealer({ commit }, payload) {
    commit("setUserDealer", payload);
  },

  clearAuthError({ commit }) {
    commit("clearAuthError");
  },

  setAuthLoading({ commit }, payload) {
    commit("setAuthLoading", payload);
  },

  setRegistrationSuccess({ commit }, payload) {
    commit("setRegistrationSuccess", payload);
  },

  setRegistrationErrors({ commit }, payload) {
    commit("setRegistrationErrors", payload);
  },

  setIsAuthenticating({ commit }, payload) {
    commit("setIsAuthenticating", payload);
  },

  cleanupAuthListeners({ commit, getters, state }, clearUser = false) {
    console.log("🧹 cleanupAuthListeners called, clearUser:", clearUser);
    if (state.cleanupInProgress) {
      console.log("⏳ Cleanup already in progress, skipping");
      return;
    }

    commit("setCleanupInProgress", true);

    // Clean up any existing user subscription
    const unsubscribe = getters.userUnsubscribe;
    if (unsubscribe) {
      console.log("🔌 Cleaning up existing Firebase listener");
      try {
        unsubscribe();
      } catch (error) {
        console.error("Error cleaning up user subscription:", error);
      }
      commit("setUserUnsubscribe", null);
    } else {
      console.log("ℹ️ No existing Firebase listener to clean up");
    }

    // Only clear current user if explicitly requested (like during logout)
    if (clearUser) {
      console.log("👤 Clearing current user data");
      commit("setCurrentUser", null);
      commit("setAdminToken", null);
    }

    // Reset the flag after a short delay
    setTimeout(() => {
      commit("setCleanupInProgress", false);
    }, 100);
  }
};

const mutations = {
  setCurrentUser(state, user) {
    state.currentUser = user;
  },
  setUserDealer(state, payload) {
    state.userDealer = payload;
  },
  setEmail(state, payload) {
    state.email = payload;
  },
  setAdminToken(state, payload) {
    state.adminToken = payload;
  },
  setToken(state, payload) {
    state.token = payload;
  },
  setAuthLoading(state, payload) {
    state.loading = payload;
  },
  setRegistrationSuccess(state, payload) {
    state.registrationSuccess = payload;
  },
  setRegistrationErrors(state, payload) {
    state.registrationErrors = payload;
  },
  setUserUnsubscribe(state, payload) {
    state.userUnsubscribe = payload;
  },
  setCleanupInProgress(state, payload) {
    state.cleanupInProgress = payload;
  },
  setIsAuthenticating(state, payload) {
    state.isAuthenticating = payload;
  },
  setLastUserRefresh(state, payload) {
    state.lastUserRefresh = payload;
  }
};

const getters = {
  currentUser(state) {
    return state.currentUser;
  },
  userDealer(state) {
    return state.userDealer;
  },
  adminToken(state) {
    return state.adminToken;
  },
  email(state) {
    return state.email;
  },
  token(state) {
    return state.token;
  },
  authLoading(state) {
    return state.loading;
  },
  registrationSuccess(state) {
    return state.registrationSuccess;
  },
  registrationErrors(state) {
    return state.registrationErrors;
  },
  userUnsubscribe(state) {
    return state.userUnsubscribe;
  },
  isAuthenticating(state) {
    return state.isAuthenticating;
  },
  lastUserRefresh(state) {
    return state.lastUserRefresh;
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
